import { accessAreaBranches, AREA_TYPE } from '@/constants/area-constants';
import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";
import { AccessAreaCard } from '../../../components/accessArea/AccessAreaCard';
import AccessAreaDetails from '../../../components/accessArea/AccessAreaDetails';
import AssignModalTwo from '../../../components/assignWizard/AssignModal';
import AssignedTab from '../../../components/assignWizard/AssignedTab';
import ManageButton from '../../../components/assignWizard/ManageButton';
import { TreeHierarchy } from '../../../components/checkbox-tree/checkboxNodeField';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import { Topic } from '../../../components/item-content-two/Topic';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { ACCESS_TYPES } from '../../../constants/access-constants';
import Commands from '../../../constants/commands';
import { AreaSubCmds } from '../../../constants/sub-commands';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { AccessArea as AccessAreaModel } from '../../../models/AccessArea';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import AccessGroupColumns from '../../../models/ColumnDefinitions/AccessGroupColumns';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { ExecuteCommand } from '../../../models/ExecuteCommand';
import { ListQuery } from '../../../models/ListQuery';
import { Lookup } from '../../../models/Lookup';
import LookupTypes from '../../../models/LookupTypes';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { Name } from '../../../models/Name';
import { TabSummary } from '../../../models/TabSummary';
import { Zone } from '../../../models/Zone';
import AccessAreaSearch from '../../../models/search/AccessAreaSearch';
import AccessGroupSearch from '../../../models/search/AccessGroupSearch';
import AccessTimePatternSearch from '../../../models/search/AccessTimePatternSearch';
import SiteSearch from '../../../models/search/SiteSearch';
import { hideColumns } from '../../../models/utilities/columnHelper';
import { deleteAreaLocation, getAccessAreaById, getAccessAreaNamesWithPagination, getAreaLocations, storeAccessArea, storeAreaLocationData } from '../../../services/accessAreaService';
import { assignUnassignAccessGroups, getAccessGroupsNamesWithPagination, getAllAccessGroups } from '../../../services/accessGroupService';
import { getAccessTimePatternNamesWithPagination } from '../../../services/accessTimePatternService';
import { getControllerTree } from '../../../services/controllerService';
import { getDoorsForAZone } from '../../../services/doorService';
import { executeCommandAreaTree } from '../../../services/executeCommandService';
import { getSiteNamesWithPagination } from '../../../services/siteService';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { getZonesForAController } from '../../../services/zoneService';
import { IAppState } from '../../../store/types';
import { getDayNames } from '../../../utilities/DateUtils';
import { buildDoors, buildZones, toggleController, toggleDoor, toggleZone } from '../../../utilities/accessAreaUtils';
import { appendFilter } from '../../../utilities/filters';
import { PaginationModel } from '@/models/Pagination';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

interface AccessAreaProps {
    areaType: AREA_TYPE;
}

const AccessArea: FC<AccessAreaProps> = ({ areaType }) => {
    const initialItem: AccessAreaModel = {
        id: 0,
        name: '',
        site: '',
        siteId: 0,
        description: '',
        doorList: [],
        doors: 0,
        doorIds: [],
        default: undefined,
    }
    const { translate } = useLocale();
    const isAccessArea = areaType === ACCESS_TYPES.ACCESS_AREA;
    const initialTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(isAccessArea ? TranslationKeys.accessGroups : TranslationKeys.floorGroups), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const site = useSelector((state: IAppState) => state.site.site);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<AccessAreaModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const [doorTree, setDoorTree] = useState<TreeHierarchy | undefined>(undefined);
    const [showManageModal, setShowManageModal] = useState(false);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const allItemsPath = '/access-control/enrolment/access-areas';
    const itemPath = isAccessArea ? '/access-control/enrolment/access-area/' : '/access-control/enrolment/lift-area/';
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find((item) => item.accessor === "name")?.searchOnName,
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.accessTimePattern,
            values: [],
            dataMethod: getAccessTimePatternNamesWithPagination,
            searchAccessor: AccessTimePatternSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.days,
            values: getDayNames(translate)
        }
    ];

    const performOperation = async (cmdSubType: string, key: string = "controllerId", nodeId: string = "", areaId: string) => {
        try {
            const executeCommandData: ExecuteCommand = {
                cmd: Commands.Maintenance.label,
                cmdSubType: cmdSubType,
                [key]: nodeId,
                areaId: areaId,
            };

            await executeCommandAreaTree(executeCommandData)

        } catch (err) {
            showError(err, toast.warn)
        }
    }

    const onNodeCheck = async (node: TreeHierarchy) => {
        try {
            let treeBranch: TreeHierarchy | undefined = undefined;

            if (doorTree) {
                if (node.branch === accessAreaBranches.CONTROLLER) {

                    const { currentBranch, isChecked } = toggleController(doorTree, node.id);

                    if (isChecked) {
                        performOperation(AreaSubCmds.ADD_CONTROLLER_AREA_LOCATION, "controllerId", node.id, itemId)
                    } else {
                        performOperation(AreaSubCmds.DELETE_CONTROLLER_AREA_LOCATION, "controllerId", node.id, itemId)
                    }

                    treeBranch = currentBranch;
                } else if (node.branch === accessAreaBranches.ZONE) {
                    const { currentBranch, isChecked } = toggleZone(doorTree, node.parentId, node.id);

                    if (isChecked) {
                        performOperation(AreaSubCmds.ADD_ZONE_AREA_LOCATION, "zoneId", node.id, itemId)
                    } else {
                        performOperation(AreaSubCmds.DELETE_ZONE_AREA_LOCATION, "zoneId", node.id, itemId)
                    }

                    loadZoneData(node);
                    treeBranch = currentBranch;
                } else if (node.branch === accessAreaBranches.LOCATION) {
                    let controllerId: string | undefined = undefined;
                    if (doorTree.options) {
                        doorTree.options.forEach(controller => {
                            const zoneExists = controller.options.some(zone => zone.id === node.parentId);
                            if (zoneExists) {
                                controllerId = controller.id;
                            }
                        });
                    }

                    const { currentBranch, isChecked, toggledIndexes } = toggleDoor(doorTree, controllerId, node.parentId, node.id);

                    if (isChecked) {

                        const locData = (node.id && await storeAreaLocationData(currentItem.id, +node.id));
                        if (locData && locData?.id && currentBranch) {
                            const currentLocationNode = currentBranch.options[toggledIndexes.controllerIndex].options[toggledIndexes.zoneIndex].options[toggledIndexes.doorIndex];
                            currentLocationNode.areaLocationId = locData.id; // attach areaLocation id, the id will be used when deleting
                        }
                    } else {
                        if (node.areaLocationId) {
                            const areaLocation = await getAreaLocations({ filters: appendFilter(node.id, "Location", `area.id==${itemId};location.id`, []) });
                            if (areaLocation && areaLocation.length > 0) {
                                await deleteAreaLocation(areaLocation[0].id); // delete by area location id
                            }
                        }
                    }
                    treeBranch = currentBranch;
                }

                if (treeBranch) {
                    setDoorTree({ ...treeBranch });
                }
            }
        } catch (error) {
            showError(error, toast.warn)
        }
    };

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const loadSiteData = async () => {
        setBusy(true)
        try {
            if (itemId) {
                const accessArea = await getAccessAreaById(itemId);
                const checked = accessArea?.doors ? (accessArea?.doors > 0) : false;
                const site: TreeHierarchy =
                {
                    name: accessArea.name,
                    label: accessArea.name,
                    options: [],
                    id: accessArea.siteId.toString(),// site id
                    branch: accessAreaBranches.AREA,
                    checked: checked,
                };

                setSelectedItem({ label: accessArea.name, value: accessArea.id.toString(), isDefault: accessArea.isDefault });
                setDoorTree(site);

                accessArea['treeChanaged'] = false;
                setCurrentItem(accessArea)
                setBreadcrumb([
                    { value: '/access-control/enrolment', displayName: translate.byKey(TranslationKeys.enrolments) },
                    { value: allItemsPath, displayName: translate.byKey(isAccessArea ? TranslationKeys.accessAreas : TranslationKeys.liftAreas) },
                    { value: itemPath + `${itemId}`, displayName: `${accessArea?.name ?? ''}` }
                ]);
                setTabs(updateTabBadgeTotal(1, (isAccessArea ? accessArea.accessGroups : accessArea.floorGroups) ?? 0, initialTabs));
                return;
            }

            setDoorTree(undefined);
            setCurrentItem(initialItem);
            setTabs([initialTabs[0]]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        } finally {
            setBusy(false)
        }

    }

    /**
     * 
     * @param node this is a current node
     * @param isExpanding checks if the node is expanding, because this method is used to load data on onNodeCheck and onToggleExpand
     */
    const loadZoneData = async (node: TreeHierarchy, isExpanding: boolean = false) => {
        try {
            if (doorTree) {
                const controllerIndex = doorTree["options"].findIndex(ctrl => ctrl.id === node.parentId);

                const zone = doorTree["options"][controllerIndex].options.find(zone => zone.id === node.id);
                const zoneIndex = doorTree["options"][controllerIndex].options.findIndex(zone => zone.id === node.id);
                if (doorTree["options"][controllerIndex].options[zoneIndex]['options'].length === 0) {
                    const linkedWithLiftReader = appendFilter(areaType !== ACCESS_TYPES.ACCESS_AREA, "Linked With Lift Readers", "linkedWithLiftReader", [])
                    const locations = (await getDoorsForAZone(zone?.id as string, { pageSize: 64, filters: linkedWithLiftReader })).rows;
                    if (locations?.length > 0) {
                        const locationIds: string[] = locations.map(loc => loc.id.toString());

                        const areaLocation = await getAreaLocations({ filters: appendFilter(locationIds, "Location", `area.id==${itemId};location.id`, []), pageSize: locationIds.length });
                        if ((areaLocation && areaLocation.length > 0) || isExpanding) {
                            const doors = buildDoors(locations, zone?.id, areaLocation);

                            doorTree["options"][controllerIndex].options[zoneIndex]['options'] = doors;
                            setDoorTree({ ...doorTree });
                        }
                    }
                }
            }
        } catch (error) {
            showError(error, toast.warn)
        }
    }

    const onToggleExpand = async (node: TreeHierarchy) => {
        try {
            if (doorTree) {
                if (node.branch === accessAreaBranches.AREA && doorTree["options"].length <= 0) {
                    let nextPage = 1;
                    let continueFetching = false;

                    do {
                        const { rows, pagination } = await getControllerTree({
                            pageSize: 100,
                            goToPage: nextPage,
                            filters: appendFilter(doorTree.id, "Site", "site.id", [])
                        }, currentItem.id);

                        if (rows?.length > 0) {
                            doorTree["options"] = [...(doorTree.options || []), ...rows];
                            setDoorTree({ ...doorTree });
                        }

                        nextPage++;
                        continueFetching = (pagination.pageNumber < pagination.totalPages);
                    } while (continueFetching);// Fetch all controller
                    return;
                }

                if (node.branch === accessAreaBranches.CONTROLLER) {

                    const controllerIndex = doorTree["options"].findIndex(ctrl => ctrl.id === node.id);
                    const currentController = doorTree["options"][controllerIndex];

                    if (currentController["options"].length === 0) {
                        const zone: Zone[] = (await getZonesForAController(currentController.id as string, { pageSize: 64 })).rows;
                        if (zone?.length > 0) {
                            const builtZones = buildZones(zone, currentController.id, itemId);
                            currentController["options"] = builtZones;
                            setDoorTree({ ...doorTree });
                        }
                    }
                    return
                }

                if (node.branch === accessAreaBranches.ZONE && node.parentId) {
                    loadZoneData(node, true);
                    return
                }
            }

        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const handleManageClick = () => {
        setShowManageModal(true);
    }

    const tabContentList: ContentWrapper[] = [
        {
            content: <AccessAreaDetails tree={doorTree} onCheck={onNodeCheck} onToggleExpand={onToggleExpand} />
        },
        {
            content: <AssignedTab
                key={`assignedGroupsForArea${itemId}`}
                column={AccessGroupColumns}
                searchColumn={AccessGroupSearch}
                dataMethod={(listParameters?: ListQuery) => getAllAccessGroups(areaType, translate, {...listParameters, filters: appendFilter(itemId, 'Area', 'area.id', listParameters?.filters ?? []) })}
                id={itemId}
                lookup={lookup}
                title={translate.byKey(isAccessArea ? TranslationKeys.assignedAccessGroups : TranslationKeys.assignedFloorGroups)}>
                <ManageButton onClick={handleManageClick} />
            </AssignedTab>
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }
    ];

    /**
    * Fetch all topics for the topic list component
    * @returns
    */
    const topic: Topic = {
        accessor: 'areaName',
        allItemsPath,
        itemPath,
        title: isAccessArea ? translate.byKey(TranslationKeys.accessAreas) : translate.byKey(TranslationKeys.liftAreas),
    }

    /**
     * Save details when the form is submitted
     * @param values
     */
    const saveAccessArea = async (values: any) => {
        const accessArea: AccessAreaModel = {
            ...currentItem,
            name: values.name,
            siteId: itemId ? values.siteId : site.value,
            id: +itemId,
            site: itemId ? values.site : site.label,
            description: values.description,
            ...(areaType !== null && { areaType: areaType }),
        };

        const createNewAccessArea = itemId ? +itemId < 0 : true;

        storeAccessArea(accessArea, createNewAccessArea).then((value: any) => {
            setItemId(value.id);
            history.push(`${itemPath}${value.id}`);
            toast.success(translate.byKey(TranslationKeys.accessAreaSaved));
            setDoorTree(undefined);
        }).catch(failure => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
        }).finally(() => {
            setBusy(false);
        });
    }

    useEffect(() => {
        loadSiteData();
    }, [itemId]);

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    const getAssignedAccessGroups = async (
        setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, 
        setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>, 
        setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
        setOriginalSelectedRows: React.Dispatch<React.SetStateAction<string[]>>,  
        setAlwaysSelectedAndDisabled?: React.Dispatch<React.SetStateAction<string[]>>) => {

        try {
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = []
                do {
                const accessGroups = (await getAllAccessGroups(areaType, translate,{ filters: itemId ? appendFilter(itemId, 'Area', 'area.id', []) : [] }));
                ids = ids.concat(accessGroups.rows.map(accessGroup => accessGroup.id.toString()));
                rows = rows.concat(accessGroups.rows);
                pagination = accessGroups.pagination;
                pageNumber++;
            }while (pageNumber <= pagination.totalPages)
            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
            setAlwaysSelectedAndDisabled && setAlwaysSelectedAndDisabled(ids);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const assignAccessGroups = (toBeAssigned: string[], toBeUnassigned: string[]) => {
        setBusy(true);
        assignUnassignAccessGroups(toBeAssigned, toBeUnassigned, itemId, "area").then((response) => {
            loadSiteData();
            toast.success(translate.byKey(TranslationKeys.success));
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            setBusy(false);
        });
    }

    /**
     *
     * @returns Inner content block
     */
    const getTabContent = (index?: number) => {
        if (itemId && tabs[index ?? 0].isActive) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }
        return tabContentList[0].content;
    };

    const getDataMethod = (listParameters?: ListQuery) => {
        return getAllAccessGroups(areaType, translate, {
            ...listParameters,
            filters: appendFilter(currentItem?.siteId, 'Site', 'area.site.id', listParameters?.filters ?? [])
        })
    };

    return (
        <div className='flex h-full'>
            <TopicContextProvider dataMethod={(listQuery?: ListQuery) => getAccessAreaNamesWithPagination(areaType, listQuery)} itemId={itemId} topic={topic} selectedItem={selectedItem}>
                <ItemContentTabTwo>
                    <SimpleItemDetails
                        breadcrumb={breadcrumb}
                        busy={busy}
                        getTabContent={getTabContent}
                        itemProfile={currentItem}
                        onBack={() => history.push(allItemsPath)}
                        saveDetails={saveAccessArea}
                        tabs={tabs}
                        itemPath={isAccessArea ? itemPath : undefined}
                        initialValues={currentItem}>
                        <AccessAreaCard />
                    </SimpleItemDetails>
                </ItemContentTabTwo>
            </TopicContextProvider>
            <AssignModalTwo
                key={`assignAccessGroupsFor${itemId}`}
                assigningTitle={translate.byKey(isAccessArea? TranslationKeys.accessGroups : TranslationKeys.floorGroups)}
                dataMethod={getDataMethod}
                getAssignedItems={getAssignedAccessGroups}
                handleSubmit={assignAccessGroups}
                isOpen={showManageModal}
                modalTitle={translate.byKey(isAccessArea ? TranslationKeys.assignAccessGroups : TranslationKeys.assignedFloorGroups)}
                onClose={() => setShowManageModal(false)}
                searchColumns={AccessGroupSearch}
                tableColumns={hideColumns(AccessGroupColumns, ["days", "startTime", "duration", "enrolments", "area"])}
                lookup={lookup} />
        </div >
    )
}

export default AccessArea;

